package ca.northroot.app.ui.map

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import ca.northroot.app.data.*
import ca.northroot.app.ui.components.*
import ca.northroot.app.ui.theme.*
import ca.northroot.app.utils.DateTimeUtils
import ca.northroot.app.utils.PlatformUtils

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LiveTeamMapScreen(
    db: Db,
    companyId: String,
    currentUserId: String,
    onBack: () -> Unit
) {
    var users by remember { mutableStateOf<List<UserProfile>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }

    LaunchedEffect(companyId) {
        isLoading = true
        db.getTeamLocations(companyId).collect { list ->
            users = list
            isLoading = false
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text("Live Team Map", fontWeight = FontWeight.Bold)
                        Text("${users.size} members active", style = MaterialTheme.typography.labelSmall, color = NorthrootGold)
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                }
            )
        }
    ) { padding ->
        Box(Modifier.fillMaxSize().padding(padding).background(MaterialTheme.colorScheme.background)) {
            if (isLoading && users.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                }
            } else if (users.isEmpty()) {
                Column(
                    modifier = Modifier.fillMaxSize().padding(32.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(Icons.Default.LocationOff, null, modifier = Modifier.size(64.dp), tint = NorthrootGray.copy(alpha = 0.5f))
                    Spacer(Modifier.height(16.dp))
                    Text("No crew members active", fontWeight = FontWeight.Bold, color = NorthrootGray)
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    items(users) { user ->
                        MemberLocationCard(user)
                    }
                    item { Spacer(Modifier.height(80.dp)) }
                }
            }
        }
    }
}

@Composable
fun MemberLocationCard(user: UserProfile) {
    NorthrootCard {
        Row(Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier.size(48.dp).clip(CircleShape).background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                Text(user.displayName.take(1).uppercase(), fontWeight = FontWeight.Black, color = MaterialTheme.colorScheme.primary)
            }
            Spacer(Modifier.width(16.dp))
            Column(Modifier.weight(1f)) {
                Text(user.displayName.ifBlank { "Crew Member" }, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium, color = MaterialTheme.colorScheme.onSurface)
                Text(
                    "Last seen: ${DateTimeUtils.formatTime(user.lastLocationUpdate)}", 
                    style = MaterialTheme.typography.bodySmall, 
                    color = NorthrootGray
                )
            }
            
            if (user.lastLatitude != null && user.lastLongitude != null) {
                IconButton(onClick = { 
                    PlatformUtils.launchMap("${user.lastLatitude},${user.lastLongitude}") 
                }) {
                    Icon(Icons.Default.Map, "View on Map", tint = MaterialTheme.colorScheme.primary)
                }
            }
        }
    }
}
